import { useEffect, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Eye, Italic, List, ListOrdered, Loader2, Save, X } from 'lucide-react';
import { documentsApi } from '../api';

const ToolbarButton = ({ label, active, onClick, children }) => (
  <button type="button" title={label} onClick={onClick} className={`p-1.5 rounded ${active ? 'bg-green-500/30 text-green-200' : 'text-gray-300 hover:bg-gray-700'}`}>{children}</button>
);

function SectionEditor({ section, onChange, readOnly = false }) {
  const editor = useEditor({
    extensions: [StarterKit],
    content: section.content_html || '<p></p>',
    editable: !readOnly,
    editorProps: { attributes: { class: 'min-h-28 px-3 py-2 outline-none text-slate-100 caret-white max-w-none [&_p]:text-slate-100 [&_li]:text-slate-100 [&_strong]:text-white [&_em]:text-slate-100 [&_ol]:text-slate-100 [&_ul]:text-slate-100' } },
    onUpdate: ({ editor: currentEditor }) => onChange({ ...section, content_html: currentEditor.getHTML() }),
  });
  useEffect(() => {
    if (editor && editor.getHTML() !== (section.content_html || '<p></p>')) editor.commands.setContent(section.content_html || '<p></p>', false);
  }, [editor, section.id, section.content_html]);
  useEffect(() => { editor?.setEditable(!readOnly); }, [editor, readOnly]);
  return (
    <article className="border border-gray-700 rounded-lg overflow-hidden bg-gray-800/50">
      <div className="p-2 flex gap-2 items-center border-b border-gray-700">
        <input disabled className="input flex-1 !py-1.5 !text-slate-100 disabled:opacity-70" value={section.title} aria-label="Tópico definido pelo modelo-base" />
      </div>
      {editor && !readOnly && <div className="px-2 py-1 border-b border-gray-700 flex gap-1">
        <ToolbarButton label="Negrito" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={16} /></ToolbarButton>
        <ToolbarButton label="Itálico" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={16} /></ToolbarButton>
        <ToolbarButton label="Lista" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={16} /></ToolbarButton>
        <ToolbarButton label="Lista numerada" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={16} /></ToolbarButton>
      </div>}
      <div className="bg-gray-950"><EditorContent editor={editor} /></div>
    </article>
  );
}

const withIds = (sections = []) => sections.map((section, index) => ({ id: section.section_id || `section-${Date.now()}-${index}-${Math.random()}`, title: section.title || '', content_html: section.content_html || '<p></p>' }));

export default function StructuredDocumentEditor({ document, onClose, onSaved }) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(null);

  useEffect(() => {
    let live = true;
    documentsApi.getContent(document.id).then(response => {
      if (live) { setSections(withIds(response.data.sections)); setRevision(response.data.revision || null); }
    }).catch(requestError => live && setError(requestError.response?.data?.detail || 'Não foi possível carregar o conteúdo.'))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [document.id]);

  const changeSection = (updated) => setSections(current => current.map(section => section.id === updated.id ? updated : section));
  const save = async () => {
    const payload = sections.filter(section => section.title.trim()).map(({ id, title, content_html }) => ({ section_id: id, title: title.trim(), content_html }));
    if (!payload.length) { setError('Inclua ao menos uma seção no documento.'); return; }
    try {
      setSaving(true); setError('');
      await documentsApi.saveContent(document.id, { sections: payload, change_notes: note.trim() || null });
      setNote(''); onSaved?.();
      // The PDF is always generated from the saved system content and the
      // referenced base template; it is never an attachment from the user.
      try {
        const response = await documentsApi.previewContent(document.id);
        window.open(URL.createObjectURL(response.data), '_blank', 'noopener,noreferrer');
      } catch {
        setError('O conteúdo foi salvo, mas a prévia do PDF não pôde ser aberta.');
      }
    } catch (saveError) { setError(saveError.response?.data?.detail || 'Não foi possível salvar a versão.'); }
    finally { setSaving(false); }
  };
  const preview = async () => {
    try {
      const response = await documentsApi.previewContent(document.id);
      window.open(URL.createObjectURL(response.data), '_blank', 'noopener,noreferrer');
    } catch (previewError) { setError(previewError.response?.data?.detail || 'Salve o documento antes de gerar a pré-visualização.'); }
  };
  return <div className="fixed inset-0 z-[70] bg-gray-950 flex flex-col" role="dialog" aria-modal="true">
    <header className="shrink-0 px-5 py-3 border-b border-gray-700 bg-gray-900 flex justify-between gap-4">
      <div><h2 className="font-semibold text-white">Documento estruturado</h2><p className="text-xs text-gray-400">{document.code} — os tópicos vêm do modelo-base; preencha somente o conteúdo</p></div>
      <button onClick={onClose} className="p-2 text-gray-400 hover:text-white"><X size={20} /></button>
    </header>
    <main className="flex-1 overflow-y-auto p-5 max-w-4xl w-full mx-auto space-y-3">
      {loading ? <div className="py-20 flex justify-center text-gray-300 gap-2"><Loader2 className="animate-spin" /> Carregando…</div> : error && !sections.length ? <p className="text-red-300">{error}</p> : <>
        {error && <p className="text-sm text-red-300 bg-red-500/10 p-3 rounded">{error}</p>}
        {revision?.edit_scope === 'topic' && <p className="text-sm text-blue-200 bg-blue-500/10 p-3 rounded">Revisão por tópico: apenas a seção selecionada pode ser alterada.</p>}
        {sections.map(section => <SectionEditor key={section.id} section={section} onChange={changeSection} readOnly={revision?.edit_scope === 'topic' && section.id !== revision.target_section_id} />)}
      </>}
    </main>
    {!loading && <footer className="shrink-0 p-3 bg-gray-900 border-t border-gray-700 flex flex-col sm:flex-row gap-2"><input value={note} onChange={event => setNote(event.target.value)} maxLength={2000} placeholder="Descrição da alteração (opcional)" className="input flex-1" /><button type="button" onClick={preview} className="btn-secondary flex items-center gap-2 justify-center"><Eye size={17} /> Pré-visualizar PDF</button><button type="button" disabled={saving} onClick={save} className="btn-primary flex items-center gap-2 justify-center">{saving ? <Loader2 className="animate-spin" size={17} /> : <Save size={17} />} Salvar versão</button></footer>}
  </div>;
}
